import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { lazy, useEffect, useState } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import { useRouteError } from "react-router";

const reloadCooldownMs = 10000;
const reloadStorageKey = "views:assetReload";

const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload/iu.test(
    message,
  );
};

/*
 * A deploy replaces hashed chunks, so a tab opened before it can no longer
 * fetch its lazy chunks. Reload to pull the fresh index.html and assets, with
 * a cooldown so a reload that does not fix the issue cannot loop.
 */
const reloadForFreshAssets = (): boolean => {
  try {
    const now = Date.now();
    const lastReload = Number(sessionStorage.getItem(reloadStorageKey) ?? "0");

    if (now - lastReload < reloadCooldownMs) {
      return false;
    }

    sessionStorage.setItem(reloadStorageKey, String(now));
  } catch {
    return false;
  }

  window.location.reload();

  return true;
};

/*
 * Vite re-throws a failed dynamic import unless the vite:preloadError handler
 * calls preventDefault. Suppress that re-throw only when we actually reload; if
 * the reload is skipped (cooldown or sessionStorage failure), let the original
 * error propagate so RouteErrorBoundary can recognize, log, and surface it
 * instead of Vite silently resolving the import to undefined.
 */
const handleVitePreloadError = (event: Event): void => {
  if (reloadForFreshAssets()) {
    event.preventDefault();
  }
};

const handleReloadClick = (): void => {
  window.location.reload();
};

const reloadingMessage = "Loading the latest version… ";

const errorMessage = (chunkError: boolean, reloading: boolean): string => {
  if (!chunkError) {
    return "Oops, something went wrong! ";
  }

  /*
   * The cooldown can skip the auto-reload (e.g. a reload already happened and
   * did not fix it). Prompt the user to reload manually instead.
   */
  return reloading
    ? reloadingMessage
    : "A new version is available. Please reload. ";
};

interface IReloadNoticeProps {
  readonly message: string;
}

const ReloadNotice = ({ message }: IReloadNoticeProps): JSX.Element => (
  <p>
    {message}
    <button onClick={handleReloadClick} type={"button"}>
      {"Reload"}
    </button>
  </p>
);

const StaleChunkNotice = (): JSX.Element => (
  <ReloadNotice message={reloadingMessage} />
);

const lazyWithReload = (
  load: () => Promise<{ default: ComponentType } | undefined>,
): LazyExoticComponent<ComponentType> =>
  lazy(async (): Promise<{ default: ComponentType }> => {
    const module = await load();

    if (module === undefined) {
      datadogLogs.logger.error("Suppressed stale chunk import; reload pending");

      return { default: StaleChunkNotice };
    }

    return module;
  });

const RouteErrorBoundary = (): JSX.Element => {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect((): void => {
    if (chunkError) {
      datadogLogs.logger.error(
        "Failed to load app chunk; reloading for fresh assets",
        {},
        error instanceof Error ? error : undefined,
      );
      setReloading(reloadForFreshAssets());
    } else {
      datadogRum.addError(error);
    }
  }, [chunkError, error]);

  return <ReloadNotice message={errorMessage(chunkError, reloading)} />;
};

export {
  handleVitePreloadError,
  isChunkLoadError,
  lazyWithReload,
  reloadForFreshAssets,
  RouteErrorBoundary,
};
